import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { DoorOpen, ChevronRight, Crown, KeyRound, Globe } from "lucide-react";
import RoomDashboardSheet from "../Components/RoomDashboardSheet";
import AccountUpgradeModal from "../Components/AccountUpgradeModal";
import LoginModal from "../Components/LoginModal";
import roomCreationService from "../Services/roomCreationService";
import useEnsureDisplayName from "../hooks/useEnsureDisplayName";
import EditNameModal from "../Components/EditNameModal";
import PlayingAsRow from "../Components/PlayingAsRow";
import useCoinStore from "../stores/coinStore";
import BottomSheet from "../Components/BottomSheet";
import SoloGameOptions from "../Components/SoloGameOptions";
import SignupNudge from "../Components/SignupNudge";
import SubscriptionModal from "../Components/SubscriptionModal";
import AnnouncementToast from "../Components/AnnouncementToast";
import RunLaunchModal from "../Components/RunLaunchModal";
import AppStorePrompt from "../Components/AppStorePrompt";
import ResumeRoomBanner from "../Components/ResumeRoomBanner";
import StatsBar from "../Components/StatsBar";
import analytics from "../Services/analyticsService";
import useHomeSideRooms from "../hooks/useHomeSideRooms";
import SeoHead from "../Components/SeoHead";
import { BotMascot, MascotPlayground, PencilMascot } from "../Components/Mascot";


// The "New" tag on NameIt Run goes away at midnight (Lagos) on 16 November 2026.
const RUN_NEW_UNTIL = new Date("2026-11-16T00:00:00+01:00");

const BOT_PILLS = [
  { name: "Kafi", color: "#10b981", label: "Easy", desc: "Good for beginners. Kafi goes slow — you'll have time to think." },
  { name: "Kosi", color: "#f59e0b", label: "Medium", desc: "Faster and sharper. Kosi won't let you slack." },
  { name: "Kemi", color: "#be185d", label: "Hard", desc: "Ruthless. Beat Kemi in a Full Game to earn coins." },
];

const ROOM_STATUS_TEXT = { waiting: "Waiting for players", starting: "Starting", playing: "Game in progress", ended: "Finished" };

const SideCard = ({ title, action, children }) => (
  <div className="hidden lg:block bg-brand-surface rounded-2xl border border-brand-border shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 mb-6">
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-[11px] font-bold text-brand-textSecondary uppercase tracking-wide">{title}</h2>
      {action}
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const SideRow = ({ title, subtitle, meta, buttonLabel, onClick, outline }) => (
  <div className="flex items-center gap-3 px-3.5 py-3 rounded-xl bg-brand-surface2">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-brand-text truncate">{title}</p>
      <p className="text-xs text-brand-textSecondary truncate">{subtitle}</p>
    </div>
    {meta && <span className="text-xs font-semibold text-brand-textMuted">{meta}</span>}
    <button
      onClick={onClick}
      className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${
        outline
          ? "border border-red-200 bg-brand-surface text-brand-accent hover:bg-red-50"
          : "bg-brand-accent text-white hover:bg-red-700"
      }`}
    >
      {buttonLabel}
    </button>
  </div>
);

const HomeRow = ({ onClick, icon, iconBg = "bg-brand-accentMuted", title, subtitle }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-3 px-4 py-3 min-h-[64px] border-b border-brand-borderLight last:border-b-0 text-left hover:bg-brand-accentMuted transition-colors"
  >
    <span className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`} aria-hidden="true">
      {icon}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-bold text-brand-text">{title}</span>
      <span className="block text-xs text-brand-textSecondary truncate">{subtitle}</span>
    </span>
    <ChevronRight size={18} className="text-brand-placeholder flex-shrink-0" aria-hidden="true" />
  </button>
);

const Home = () => {
  const location = useLocation();
  const {
    displayName, draftName, setDraftName, showEditName, setShowEditName, ensureName, syncToAccount,
  } = useEnsureDisplayName();
  const [isCreating, setIsCreating] = useState(false);
  // "Create Public Room" on the empty public-rooms list arrives with public on.
  const [isPublicRoom, setIsPublicRoom] = useState(!!location.state?.publicRoom);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const openPro = (source) => {
    setShowSubscriptionModal(true);
    analytics.track("pro_upsell_opened", { source });
  };
  const [error, setError] = useState("");
  const [showDashboard, setShowDashboard] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [playMode, setPlayMode] = useState("multiplayer");
  const [selectedBot, setSelectedBot] = useState("Kafi");
  const [showSoloOptions, setShowSoloOptions] = useState(false);

  const navigate = useNavigate();
  const { isPro } = useCoinStore();
  const { publicRooms, recentRooms } = useHomeSideRooms();
  const showRecentCard = publicRooms.length === 0 && recentRooms.length > 0;

  const openRecentRoom = (roomId) => {
    localStorage.setItem("currentRoomId", roomId);
    navigate("/create-room");
  };

  const handleUpgradeRequest = () => {
    setShowDashboard(false);
    setShowUpgradeModal(true);
  };

  const handleUpgradeSuccess = (result) => {
    setShowUpgradeModal(false);
    alert(result.message);
  };

  const handleLoginSuccess = (result) => {
    setShowLoginModal(false);
    alert(result.message);
  };

  const createRoom = async () => {
    let name;
    try {
      name = await ensureName();
    } catch (err) {
      setError(err.message);
      return;
    }
    setIsCreating(true);
    setError("");
    try {
      const result = await roomCreationService.createRoom(name, {
        isPublic: isPublicRoom,
        maxPlayers: isPro ? 10 : 5,
      });
      if (result.success) {
        syncToAccount();
        navigate("/create-room");
      } else {
        setError(result.message);
      }
    } catch {
      setError("Failed to create room. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <SeoHead
        title="NameIt: Name, Place, Animal, Thing Online With Friends"
        description="Play the classic Name, Place, Animal, Thing category game free in your browser. Create a private room, invite friends, or play solo against AI."
        path="/"
      />
      <AnnouncementToast />
      <RunLaunchModal />
      <MascotPlayground />

      {/* Main Content */}
      <div className="container mx-auto px-4">
        <StatsBar />
        <div className="max-w-md lg:max-w-5xl mx-auto">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start lg:pt-4">
        <div className="min-w-0">

          {/* Hero */}
          {/* Bottom padding leaves room for the mascot perched on the card */}
          <div className="text-center lg:text-left pt-6 pb-10">
            <h1 className="text-3xl lg:text-4xl font-black text-red-700 tracking-tight">
              Name, Place, Animal, Thing
            </h1>
            <p className="text-brand-textSecondary text-sm lg:text-base mt-1.5 max-w-xs lg:max-w-sm mx-auto lg:mx-0">
              The classic game, online. Pick a letter, race the clock, compare answers with friends.
            </p>
          </div>

          {/* Resume Active Room */}
          <ResumeRoomBanner />

          {/* Play card — the floating mascot lives on its top edge */}
          <div data-mascot-shelf className="relative bg-brand-surface rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] border border-brand-border p-4 mb-6">
            <span data-mascot-home aria-hidden="true" className="absolute right-10 top-0 w-0 h-0" />
            {/* Segmented mode switch: one track, the chosen side lifts out */}
            <div role="group" aria-label="Play mode" className="flex gap-1 p-1 mb-4 bg-gray-100 rounded-xl">
              {[
                { key: "multiplayer", label: "Play with Friends" },
                { key: "solo", label: "Play Solo" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  aria-pressed={playMode === key}
                  onClick={() => { setPlayMode(key); setError(""); }}
                  className={`flex-1 min-h-[40px] rounded-lg text-sm font-semibold transition-colors ${
                    playMode === key
                      ? "bg-white text-brand-accent shadow-sm"
                      : "text-brand-textSecondary hover:text-brand-text"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-xl mb-4 text-sm text-center">
                {error}
              </div>
            )}

            {/* Multiplayer Content */}
            {playMode === "multiplayer" && (
              <div className="space-y-3">
                {displayName ? (
                  <PlayingAsRow name={displayName} onChange={() => setShowEditName(true)} />
                ) : (
                  <input
                    type="text"
                    aria-label="Your name"
                    className="w-full border-2 border-gray-200 rounded-2xl py-3.5 px-5 text-lg text-center focus:ring-2 focus:ring-red-400 focus:border-red-400 outline-none transition-all placeholder:text-gray-500"
                    placeholder="What's your name?"
                    value={draftName}
                    onChange={(e) => { setDraftName(e.target.value); setError(""); }}
                  />
                )}

                {/* Name, then public or private, then create: the order the
                    choices are made in. The label stays fixed so "on" always
                    means public; the line under it says who can join. */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={isPublicRoom}
                  className="w-full flex items-center justify-between gap-3 px-1 min-h-[44px] text-left"
                  onClick={() => setIsPublicRoom((v) => !v)}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-brand-text">Make room public</span>
                    <span className="block text-xs text-brand-textSecondary">
                      {isPublicRoom ? "Anyone can find and join" : "Only people with your link can join"}
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                      isPublicRoom ? "bg-red-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        isPublicRoom ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </span>
                </button>

                <button
                  onClick={createRoom}
                  disabled={isCreating}
                  className={`w-full py-4 rounded-2xl font-bold text-xl text-white ${
                    isCreating
                      ? "bg-gray-400 cursor-not-allowed"
                      : "btn-press bg-brand-accent hover:bg-red-700"
                  }`}
                >
                  {isCreating ? (
                    <span className="flex items-center justify-center gap-3">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    "Create Room"
                  )}
                </button>

                <div className="border-t border-brand-borderLight pt-3 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => navigate("/join-room")}
                    className="flex items-center justify-center gap-1.5 min-h-[48px] rounded-xl text-sm font-semibold text-brand-accent border border-red-200 hover:bg-red-50 transition-colors"
                  >
                    <KeyRound size={16} aria-hidden="true" />
                    Join with code
                  </button>
                  <button
                    onClick={() => navigate("/rooms")}
                    className="flex items-center justify-center gap-1.5 min-h-[48px] rounded-xl text-sm font-semibold text-brand-accent border border-red-200 hover:bg-red-50 transition-colors"
                  >
                    <Globe size={16} aria-hidden="true" />
                    Find a game
                  </button>
                </div>
              </div>
            )}

            {/* Solo Content */}
            {playMode === "solo" && (
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-brand-textSecondary mb-3 text-center">Pick a bot, then race to fill every category before the timer runs out.</p>

                  <div className="flex gap-2">
                    {BOT_PILLS.map((bot) => {
                      const isSelected = selectedBot === bot.name;
                      return (
                        <button
                          key={bot.name}
                          onClick={() => setSelectedBot(bot.name)}
                          aria-pressed={isSelected}
                          className={`flex-1 rounded-2xl p-3 text-center transition-all border-2 ${
                            isSelected
                              ? "bg-white shadow-sm"
                              : "bg-gray-50 border-transparent"
                          }`}
                          style={isSelected ? { borderColor: bot.color } : {}}
                        >
                          <div className="w-11 mx-auto mb-1">
                            <BotMascot bot={bot.name} state={isSelected ? "won" : "idle"} size={44} />
                          </div>
                          <span className="text-xs font-bold text-gray-800 block">{bot.name}</span>
                          <span className="text-xs text-gray-600 block">{bot.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected bot description */}
                  <p className="text-xs text-gray-600 mt-2.5 text-center min-h-[2.5rem] px-2">
                    {BOT_PILLS.find(b => b.name === selectedBot)?.desc}
                  </p>
                </div>

                <button
                  onClick={() => setShowSoloOptions(true)}
                  className="btn-press w-full py-4 rounded-2xl font-bold text-xl text-white bg-brand-accent hover:bg-red-700"
                >
                  Play vs {selectedBot} →
                </button>
              </div>
            )}
          </div>

        </div>

        <div className="min-w-0 lg:pt-6">
          {publicRooms.length > 0 && (
            <SideCard
              title="Public games open now"
              action={
                <button onClick={() => navigate("/rooms")} className="text-sm font-semibold text-brand-accent hover:text-red-700">
                  See all
                </button>
              }
            >
              {publicRooms.map((room) => (
                <SideRow
                  key={room.roomId}
                  title={room.roomName}
                  subtitle={(room.categories || []).slice(0, 4).join(" · ") || `Hosted by ${room.hostName}`}
                  meta={`${room.playerCount}/${room.maxPlayers}`}
                  buttonLabel="Join"
                  onClick={() => navigate(`/join-room/${room.roomId}`)}
                />
              ))}
            </SideCard>
          )}

          {showRecentCard && (
            <SideCard
              title="Your recent rooms"
              action={
                <button onClick={() => setShowDashboard(true)} className="text-sm font-semibold text-brand-accent hover:text-red-700">
                  My Rooms
                </button>
              }
            >
              {recentRooms.map((room) => (
                <SideRow
                  key={room.roomId}
                  title={room.roomName}
                  subtitle={`${ROOM_STATUS_TEXT[room.status] || "Room"} · ${room.playerCount} player${room.playerCount === 1 ? "" : "s"}`}
                  buttonLabel={room.status === "ended" ? "View" : "Open"}
                  onClick={() => openRecentRoom(room.roomId)}
                  outline
                />
              ))}
            </SideCard>
          )}

          {/* Everything else, as one list in the same style as Settings,
              instead of a stack of differently coloured cards. */}
          <h2 className="text-[11px] font-bold text-brand-textSecondary uppercase tracking-wide mb-2 px-1">
            More
          </h2>
          <div data-mascot-shelf className="bg-brand-surface rounded-2xl border border-brand-border shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden mb-6">
            <HomeRow
              onClick={() => {
                analytics.track("run_entry_tapped", { source: "home_card" });
                navigate("/run", { state: { from: "home_card" } });
              }}
              icon={<PencilMascot mood="celebrate" size={22} shadow={false} label="" />}
              iconBg="bg-amber-50"
              title={
                <>
                  NameIt Run
                  {new Date() < RUN_NEW_UNTIL && (
                    <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-white bg-red-600 rounded px-1.5 py-0.5 align-middle">New</span>
                  )}
                </>
              }
              subtitle="A new way to play NameIt. Run, dodge, and find the right words."
            />
            {!isPro && (
              <HomeRow
                onClick={() => openPro("home_card")}
                icon={<Crown size={18} className="text-amber-500" />}
                iconBg="bg-amber-50"
                title="NameIt Pro"
                subtitle="Unlock the full NameIt experience."
              />
            )}
            {/* The header dropdown's old shortcut into RoomDashboardSheet. */}
            <div className={showRecentCard ? "lg:hidden" : ""}>
              <HomeRow
                onClick={() => setShowDashboard(true)}
                icon={<DoorOpen size={18} className="text-brand-accent" />}
                title="My Rooms"
                subtitle="Pick up where your rooms left off."
              />
            </div>
          </div>
        </div>
        </div>

          <AppStorePrompt />

          {/* Quiet text links. They stay real <a>s (react-router's Link) so
              crawlers can follow them; Terms, Privacy, Refunds and Pricing
              are linked from Settings, the legal pages and the sitemap. */}
          <footer className="text-center pb-2">
            <nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-brand-textSecondary">
              <Link to="/solo" className="underline-offset-2 hover:underline hover:text-brand-text">Play Solo</Link>
              <Link to="/run" className="underline-offset-2 hover:underline hover:text-brand-text">NameIt Run</Link>
              <Link to="/how-to-play" className="underline-offset-2 hover:underline hover:text-brand-text">How to Play &amp; Rules</Link>
            </nav>
          </footer>

        </div>
      </div>

      {/* Bottom Sheet Modals */}
      <RoomDashboardSheet
        isOpen={showDashboard}
        onClose={() => setShowDashboard(false)}
        onUpgradeRequest={handleUpgradeRequest}
        onViewAll={() => { setShowDashboard(false); navigate("/my-rooms"); }}
        userName={displayName || "Player"}
      />

      <BottomSheet
        isOpen={showSoloOptions}
        onClose={() => setShowSoloOptions(false)}
        title={`Play vs ${selectedBot}`}
        subtitle="Pick a game mode and go"
      >
        <SoloGameOptions bot={selectedBot.toLowerCase()} />
      </BottomSheet>

      <AccountUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSuccess={handleUpgradeSuccess}
        onSwitchToSignIn={() => {
          setShowUpgradeModal(false);
          setShowLoginModal(true);
        }}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={handleLoginSuccess}
        onSwitchToSignUp={() => {
          setShowLoginModal(false);
          setShowUpgradeModal(true);
        }}
      />

      <SignupNudge onSignUp={() => setShowUpgradeModal(true)} />

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />

      <EditNameModal isOpen={showEditName} onClose={() => setShowEditName(false)} />
    </>
  );
};

export default Home;
